import type { DashboardStats } from '@postpilot/core/domain';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  FileText,
  PenSquare,
  Plus,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/primitives';
import { EmptyState, ErrorState, StatCardSkeleton } from '@/components/ui/states';
import { StatusBadge } from '@/components/ui/status-badge';
import { api, ApiError } from '@/lib/api';
import { formatScheduledAt } from '@/lib/dayjs';
import { cn } from '@/lib/utils';
import { selectBrokenAccounts, useAccountsStore } from '@/stores/accounts-store';
import { usePostsStore } from '@/stores/posts-store';

export function DashboardPage() {
  const navigate = useNavigate();

  const accounts = useAccountsStore((s) => s.accounts);
  const brokenAccounts = useAccountsStore(useShallow(selectBrokenAccounts));
  const connect = useAccountsStore((s) => s.connect);

  const posts = usePostsStore((s) => s.posts);
  const fetchPosts = usePostsStore((s) => s.fetch);

  const [stats, setStats] = React.useState<DashboardStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [next] = await Promise.all([api.stats(), fetchPosts()]);
      setStats(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your dashboard.');
    } finally {
      setLoading(false);
    }
  }, [fetchPosts]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const upcoming = posts
    .filter((p) => p.status === 'scheduled' || p.status === 'processing')
    .slice(0, 5);

  if (error) {
    return <ErrorState message={error} onRetry={() => void load()} />;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Everything across your connected accounts, at a glance.
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void connect('/accounts')}>
            <Plus />
            Connect account
          </Button>
          <Button onClick={() => navigate('/compose')}>
            <PenSquare />
            Compose
          </Button>
        </div>
      </div>

      {/* A broken account is the one problem that silently breaks scheduling.
          It gets a banner, not a badge buried in a list. */}
      {brokenAccounts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-status-failed/25 bg-status-failed/5 flex items-start gap-3 rounded-lg border p-4"
        >
          <AlertTriangle className="text-status-failed mt-0.5 size-4 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              {brokenAccounts.length} account{brokenAccounts.length === 1 ? '' : 's'} need
              reconnecting
            </p>
            <p className="text-muted-foreground mt-0.5 text-sm">
              Scheduled posts to {brokenAccounts.map((a) => `@${a.username}`).join(', ')} will fail
              until you reconnect.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/accounts')}>
            Fix
          </Button>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard
              label="Connected accounts"
              value={stats?.connectedAccounts ?? 0}
              icon={Users}
              hint={
                brokenAccounts.length > 0
                  ? `${brokenAccounts.length} need attention`
                  : 'All healthy'
              }
              tone={brokenAccounts.length > 0 ? 'warning' : 'default'}
              index={0}
            />
            <StatCard
              label="Scheduled"
              value={stats?.scheduledPosts ?? 0}
              icon={CalendarClock}
              hint="Waiting to go out"
              index={1}
            />
            <StatCard
              label="Published"
              value={stats?.publishedLast30Days ?? 0}
              icon={CheckCircle2}
              hint="Last 30 days"
              index={2}
            />
            <StatCard
              label="Needs attention"
              value={stats?.failedPosts ?? 0}
              icon={AlertTriangle}
              hint={stats?.failedPosts ? 'Failed or partly sent' : 'Nothing failed'}
              tone={stats?.failedPosts ? 'danger' : 'default'}
              index={3}
            />
          </>
        )}
      </div>

      {/* Up next */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Up next</h2>
          {upcoming.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/queue')}>
              View queue
            </Button>
          )}
        </div>

        <Card>
          {accounts.length === 0 ? (
            <EmptyState
              icon={Users}
              title="Connect your first X account"
              description="PostPilot posts on your behalf. Connect an account and you can compose, schedule and bulk-post from here."
              action={{ label: 'Connect X account', onClick: () => void connect('/accounts') }}
            />
          ) : upcoming.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Nothing scheduled"
              description="Write a post and schedule it — it will appear here and publish automatically."
              action={{ label: 'Compose a post', onClick: () => navigate('/compose') }}
            />
          ) : (
            <CardContent className="divide-y p-0">
              {upcoming.map((post) => (
                <button
                  key={post.id}
                  onClick={() => navigate('/queue')}
                  className="hover:bg-muted/40 flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{post.content || 'Empty draft'}</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      {formatScheduledAt(post.scheduledAt)} · {post.targets.length} account
                      {post.targets.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <StatusBadge status={post.status} />
                </button>
              ))}
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  tone = 'default',
  index,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  hint: string;
  tone?: 'default' | 'warning' | 'danger';
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      // Staggered so the row of cards arrives as a wave rather than a block.
      // 40ms is below the threshold where it reads as "slow" but above the one
      // where it reads as "simultaneous".
      transition={{ duration: 0.3, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
    >
      <Card className="transition-shadow hover:shadow-md">
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <span className="text-muted-foreground text-xs font-medium">{label}</span>
            <div
              className={cn(
                'flex size-7 items-center justify-center rounded-lg',
                tone === 'danger' && 'bg-status-failed/10 text-status-failed',
                tone === 'warning' && 'bg-status-processing/10 text-status-processing',
                tone === 'default' && 'bg-muted text-muted-foreground',
              )}
            >
              <Icon className="size-3.5" />
            </div>
          </div>

          {/* `tabular-nums` so the digits don't jitter horizontally when a value
              changes from 9 to 10 under a realtime update. */}
          <div className="mt-3 text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
          <div className="text-muted-foreground mt-1 text-xs">{hint}</div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
